/**
 * Ticket Lifecycle v2 — FOH Timer Handoff (Batch 3)
 *
 * React Query hook backing the manager-facing "Advisor Performance"
 * Analytics tab. Fetches the four data slices the pure
 * `computeShopAdvisorAnalytics` helper needs:
 *
 *   1. ticket_custody_events — shop-wide, the four cycle kinds, with the
 *      Batch 1/2 `reason` + `metadata` so trigger (pause/stop/manual)
 *      classification is possible.
 *   2. advisor_action_events — shop-wide: ticket_accepted,
 *      kicked_back_to_tech, ticket_kicked_to_foh.
 *   3. ticket_custody — current snapshot rows in the three FOH-attention
 *      states (for live queue counts + aging).
 *   4. advisor display names — `memberships` ⋈ `profiles` for the
 *      FOH-capable roles, reusing `toFohCapableMember` so the name
 *      resolution matches the Reassign-Advisor picker.
 *
 * Gated by the `advisorTimingAnalytics` feature flag (same gate as the
 * Passport `useAdvisorMetrics`). When off, `enabled: false` — zero
 * network, zero cost. No mock data: when the flag is on but the shop has
 * no events, the helper returns zeroed counts and `null` averages, which
 * the UI renders as graceful empty / "—" states.
 *
 * Scope: shop-only (single shopId). No district/region/org rollup.
 *
 * Boundary: reads custody / advisor / membership tables only. No
 * scheduling, timer, parking, rollover, bay, or invariants imports.
 */
import { fetchMembershipsActingAs } from '@/lib/roles/membershipsActingAs';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchPersonDisplayNames } from '@/lib/people/personDisplayName';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import {
  computeShopAdvisorAnalytics,
  SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS,
  type ShopAdvisorAnalytics,
  type ShopCustodyEventInput,
  type ShopAdvisorActionInput,
  type ShopCustodySnapshotRow,
} from '@/lib/tickets/lifecycle/shopAdvisorAnalytics';
import {
  FOH_CAPABLE_ROLES,
  toFohCapableMember,
} from '@/hooks/tickets/useFohCapableMembers';
import type { AdvisorActionKind, CustodyEventKind, CustodyStatus } from '@/lib/tickets/lifecycle/types';

interface UseShopAdvisorAnalyticsArgs {
  shopId: string | null | undefined;
}

const WINDOW_MS = SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS * 24 * 60 * 60 * 1000;

const CUSTODY_CYCLE_KINDS: CustodyEventKind[] = [
  'kick_to_foh',
  'foh_accept',
  'kick_to_boh',
  'boh_accept',
];

const ADVISOR_ACTION_KINDS: AdvisorActionKind[] = [
  'ticket_accepted',
  'kicked_back_to_tech',
  'ticket_kicked_to_foh',
];

const SNAPSHOT_STATUSES: CustodyStatus[] = [
  'pending_advisor_accept',
  'with_advisor',
  'pending_tech_accept',
];

export function useShopAdvisorAnalytics({
  shopId,
}: UseShopAdvisorAnalyticsArgs) {
  const advisorTimingAnalyticsEnabled = useFeatureFlag('advisorTimingAnalytics');
  const enabled = advisorTimingAnalyticsEnabled && !!shopId;

  // Stable window cutoff for the query cache key (matches useAdvisorMetrics).
  const windowStart = useMemo(
    () => new Date(Date.now() - WINDOW_MS).toISOString(),
    [],
  );

  return useQuery({
    queryKey: ['shop-advisor-analytics', shopId, windowStart],
    enabled,
    retry: false,
    queryFn: async (): Promise<ShopAdvisorAnalytics> => {
      // 1. Custody cycle events (with reason + metadata for triggers).
      const { data: custodyRows, error: custodyErr } = await (supabase as any)
        .from('ticket_custody_events')
        .select('event_kind, ticket_id, actor_id, reason, metadata, created_at')
        .eq('shop_id', shopId)
        .in('event_kind', CUSTODY_CYCLE_KINDS)
        .gte('created_at', windowStart);
      if (custodyErr) {
        console.error('[shop-advisor-analytics] custody events query failed', custodyErr);
        throw custodyErr;
      }
      const custodyEvents: ShopCustodyEventInput[] = (custodyRows ?? []).map(
        (r: {
          event_kind: string;
          ticket_id: string;
          actor_id: string | null;
          reason: string | null;
          metadata: unknown;
          created_at: string;
        }) => ({
          event_kind: r.event_kind as CustodyEventKind,
          ticket_id: r.ticket_id,
          actor_id: r.actor_id,
          reason: r.reason,
          metadata: r.metadata,
          created_at: r.created_at,
        }),
      );

      // 2. Advisor action events (counts + attributable kick_to_foh).
      const { data: actionRows, error: actionErr } = await (supabase as any)
        .from('advisor_action_events')
        .select('action_kind, advisor_user_id, ticket_id, created_at')
        .eq('shop_id', shopId)
        .in('action_kind', ADVISOR_ACTION_KINDS)
        .gte('created_at', windowStart);
      if (actionErr) {
        console.error('[shop-advisor-analytics] action events query failed', actionErr);
        throw actionErr;
      }
      const advisorActionEvents: ShopAdvisorActionInput[] = (actionRows ?? []).map(
        (r: {
          action_kind: string;
          advisor_user_id: string | null;
          ticket_id: string;
          created_at: string;
        }) => ({
          action_kind: r.action_kind as AdvisorActionKind,
          advisor_user_id: r.advisor_user_id,
          ticket_id: r.ticket_id,
          created_at: r.created_at,
        }),
      );

      // 3. Current custody snapshot (live queue + aging).
      const { data: snapRows, error: snapErr } = await (supabase as any)
        .from('ticket_custody')
        .select('custody_status, holder_user_id, entered_at')
        .eq('shop_id', shopId)
        .in('custody_status', SNAPSHOT_STATUSES);
      if (snapErr) {
        console.error('[shop-advisor-analytics] snapshot query failed', snapErr);
        throw snapErr;
      }
      const snapshotRows: ShopCustodySnapshotRow[] = (snapRows ?? []).map(
        (r: { custody_status: string; holder_user_id: string | null; entered_at: string }) => ({
          custody_status: r.custody_status as CustodyStatus,
          holder_user_id: r.holder_user_id,
          entered_at: r.entered_at,
        }),
      );

      // 4. Advisor display names (reuse the reassign-picker mapper).
      const advisorNames: Record<string, string> = {};
      // Multi-role: effective roles — an advisor stored as TECHNICIAN used to show as a short id.
      let memberRows: Array<{ user_id: string; role: string }> | null = null;
      let memberErr: unknown = null;
      try {
        memberRows = await fetchMembershipsActingAs(supabase, shopId, FOH_CAPABLE_ROLES);
      } catch (e) {
        memberErr = e;
      }
      if (memberErr) {
        // Names are non-critical — degrade to short ids rather than fail.
        console.warn('[shop-advisor-analytics] membership names query failed', memberErr);
      } else {
        // Names through the canonical person-name rule (no profiles embed: it
        // resolved through memberships.removed_by and came back null).
        const rows = (memberRows ?? []) as Array<{ user_id: string; role: string }>;
        const names = await fetchPersonDisplayNames(supabase, rows.map((r) => r.user_id), shopId);
        for (const row of rows) {
          const mapped = toFohCapableMember(row, names[row.user_id] ?? null);
          if (mapped) advisorNames[mapped.userId] = mapped.displayName;
        }
      }

      return computeShopAdvisorAnalytics({
        custodyEvents,
        advisorActionEvents,
        snapshotRows,
        advisorNames,
        referenceTime: new Date().toISOString(),
      });
    },
  });
}
